import axios from '@fs/zion-axios'
import zionDebug from '@fs/zion-debug'

const debug = zionDebug('standards:authorities-admin')

let uniqueId = 0

export function getXRequestId(): string {
  const now = Date.now()
  return `${now}-${++uniqueId}-places-react`
}

type PermissionsResult = Record<string, boolean>

type ErrorResult = {
  err: {
    status: number
  }
}

/**
 * Get authorization for multiple permissions within a context.
 * @return CAS Authorization object.
 */
export default async function getPlacePermissions(): Promise<PermissionsResult | ErrorResult> {
  const context = 'place'
  const permsToCheck = [
    'PERM_ATTR_COPYRIGHT_CREATE',
    'PERM_ATTR_COPYRIGHT_DELETE',
    'PERM_PLACE_VARIANT_CREATE',
    'PERM_PLACE_VARIANT_MODIFY',
    'PERM_PLACE_DATES',
    'PERM_REP_CREATE_PROVISIONAL',
    'PERM_REP_CREATE_CONFIRMED',
    'PERM_REP_COORDINATES',
    'PERM_REP_PREFERRED_LOCALE',
    'PERM_REP_PLACE_TYPE',
    'PERM_REP_FROM_TO_YEAR',
    'PERM_REP_DISPLAY_NAME_CREATE',
    'PERM_REP_DISPLAY_NAME_MODIFY',
    'PERM_REP_PREFERRED_BOUNDARY',
    'PERM_REP_DELETE',
    'PERM_REP_DELETE_CONFIRMED',
    'PERM_REP_DELETE_PROVISIONAL',
    'PERM_REP_PARENT',
    'PERM_REP_CHILD_CONSTRAINT',
    'PERM_REP_PROVISIONAL_TO_CONFIRMED',
    'PERM_REP_CONFIRMED_TO_PROVISIONAL',
    'PERM_REP_CONFIRMED_TO_CERTIFIED',
    'PERM_REP_CERTIFIED_TO_CONFIRMED',
    'PERM_ATTRIBUTE_CREATE',
    'PERM_ATTRIBUTE_MODIFY',
    'PERM_CITATION_CREATE',
    'PERM_CITATION_MODIFY',
    'PERM_EXT_XREF_CREATE',
    'PERM_EXT_XREF_MODIFY',
    'PERM_ALT_JURISDICTION_CREATE',
    'PERM_ALT_JURISDICTION_MODIFY',
    'PERM_BOUNDARY_CREATE',
    'PERM_BOUNDARY_MODIFY',
    'PERM_TOP_LEVEL_SEARCH',
    'PERM_TYPE_AND_TERM',
    'PERM_TYPE_GROUP',
    'PERM_TYPE_GROUP_MEMBER',
    'PERM_REP_GROUP',
    'PERM_REP_GROUP_MEMBER',
    'PERM_SOURCE_CREATE',
    'PERM_SOURCE_UPDATE',
    'PERM_SOURCE_DELETE',
    'PERM_PLACES_ADMIN_APP',
  ]

  // Create AuthorizationBatchRequest format for new CAS v2 endpoint
  const authorizationBatchRequest = {
    authorizationRequests: [
      {
        context,
        permissions: permsToCheck,
      },
    ],
  }

  const editAuthUrl = '/service/ident/cas/cas-public-api/authorizations'
  let resp
  try {
    resp = await axios.post(editAuthUrl, authorizationBatchRequest, {
      headers: {
        accept: 'application/cas-v2+json',
        'Content-Type': 'application/json',
        'X-Request-ID': getXRequestId(),
      },
    })
  } catch (err) {
    debug('Error while checking CAS permissions:', err)
    const error = err as { response?: { status?: number }; status?: number }
    return { err: { status: error.response?.status || error.status || 500 } }
  }
  debug('resp checking CAS permissions:', resp)

  const authorizationResult = resp.data.results[0]
  const permissions: PermissionsResult = {}

  // Set authorized permissions to true
  authorizationResult.authorizedPermissions.forEach((perm: string) => {
    permissions[perm] = true
  })

  // Set unauthorized permissions to false
  authorizationResult.unauthorizedPermissions.forEach((perm: string) => {
    permissions[perm] = false
  })
  debug('permissions:', permissions)

  return permissions
}
